import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type Phaser from 'phaser';
import type { BillionaireBonusResult, BillionaireQuestionResult, FinishPlayAttemptInput } from '../../../shared/results';
import {
  applyBonusEffect,
  applyQuestionAnswer,
  buildBonusSchedule,
  createBonusDeck,
  formatTurkishLira,
  initialEconomyState,
  type BillionaireBonusEffect,
  type BillionaireEconomyState,
} from '../../../shared/billionaire';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import { EventBus } from '../../game/EventBus';
import { BillionaireAudio } from './BillionaireAudio';
import { BillionaireEmblem, BillionaireTransition, type BillionaireTransitionInfo } from './BillionaireEmblem';
import { bonusShuffleTiming, planBonusShuffle, slotsAfter, BONUS_CARD_COUNT, type BonusShufflePlan } from './domain/bonusShuffle';
import type { BillionaireQuizConfig } from './domain/billionaireLevels';
import type { BillionaireStageMood } from './game/BillionaireStageScene';

type Phase = 'intro' | 'transition' | 'question-enter' | 'awaiting-answer' | 'lock' | 'feedback'
  | 'bonus-preview' | 'bonus-flip' | 'bonus-shuffle' | 'bonus-awaiting-pick' | 'bonus-reveal' | 'final';
type Props = { config: BillionaireQuizConfig; attemptId?: string; label?: string; restartEnabled?: boolean; onFinished?: (result: FinishPlayAttemptInput) => void };
type PendingTimer = { remaining: number; id: number; callback: () => void };
type PendingAnswer = { record: BillionaireQuestionResult; calculation: ReturnType<typeof applyQuestionAnswer> };

const LETTERS = ['A', 'B', 'C', 'D'];
/** Doğru cevabın açıklandıktan sonra görünme süresi (oyun kuralı: 2 sn). */
const QUESTION_FEEDBACK_MS = 2_000;
/** Sorular, bonus turları ve final öncesi logolu geçiş. */
const TRANSITION_MS = 1_600;
/** Cevap kilitlendikten sonra açıklamadan önceki gerilim. */
const LOCK_SUSPENSE_MS = 1_200;
const BONUS_REVEAL_MS = 1_800;
/** Kasa sayacının artış animasyonu. */
const BANK_COUNT_MS = 900;

function answerLabel(text: string, hasMedia: boolean) { return text.trim() || (hasMedia ? 'Görsel seçenek' : 'Boş seçenek'); }
function bonusLabel(effect: BillionaireBonusEffect, nextPrize: number) {
  if (effect === 'cash-half-next') return `Küçük Kasa: +${formatTurkishLira(nextPrize * 0.5)}`;
  if (effect === 'cash-next') return `Büyük Kasa: +${formatTurkishLira(nextPrize)}`;
  if (effect === 'lose-quarter') return 'Risk Kartı: kasanın %25’i gider';
  if (effect === 'next-x2') return 'İkiye Katla: sonraki doğru ödül x2';
  return 'Üçe Katla: sonraki doğru ödül x3';
}
/** Açık kartlarda görünen kısa etiket. */
function bonusFace(effect: BillionaireBonusEffect) {
  if (effect === 'cash-half-next') return { big: '+½', small: 'ÖDÜL', tone: 'good' };
  if (effect === 'cash-next') return { big: '+1', small: 'TAM ÖDÜL', tone: 'good' };
  if (effect === 'lose-quarter') return { big: '−%25', small: 'RİSK', tone: 'bad' };
  if (effect === 'next-x2') return { big: '×2', small: 'SONRAKİ', tone: 'good' };
  return { big: '×3', small: 'SONRAKİ', tone: 'best' };
}
const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function BillionaireQuizGame({ config, attemptId, label = 'Kim Milyarder Olmak İster? oyun alanı', restartEnabled = true, onFinished }: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const audioRef = useRef(new BillionaireAudio());
  const onFinishedRef = useRef(onFinished);
  const questionStartedAt = useRef(performance.now());
  const runStartedAt = useRef(performance.now());
  const resultSent = useRef(false);
  const pendingTimer = useRef<PendingTimer | null>(null);
  const pendingAnswer = useRef<PendingAnswer | null>(null);
  const [phase, setPhase] = useState<Phase>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [economy, setEconomy] = useState<BillionaireEconomyState>(initialEconomyState());
  const [displayBank, setDisplayBank] = useState(0);
  const [questionResults, setQuestionResults] = useState<BillionaireQuestionResult[]>([]);
  const [bonusResults, setBonusResults] = useState<BillionaireBonusResult[]>([]);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [selectedBonus, setSelectedBonus] = useState<number | null>(null);
  const [activeDeck, setActiveDeck] = useState<BillionaireBonusEffect[]>([]);
  const [shufflePlan, setShufflePlan] = useState<BonusShufflePlan | null>(null);
  const [shuffleStep, setShuffleStep] = useState(0);
  const [transition, setTransition] = useState<(BillionaireTransitionInfo & { next: () => void }) | null>(null);
  const [liveMessage, setLiveMessage] = useState('Yarışma sahnesi hazır.');
  const [musicMuted, setMusicMuted] = useState(() => audioRef.current.isMusicMuted());
  const [fxMuted, setFxMuted] = useState(() => audioRef.current.isFxMuted());
  const schedule = useMemo(() => buildBonusSchedule(config.questions.length), [config.questions.length]);
  const seedKey = `${config.id}:${attemptId ?? `preview-${config.id}`}`;
  const current = config.questions[questionIndex];

  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);

  useLayoutEffect(() => {
    if (!stageRef.current) return;
    void import('./game/startBillionaireStage').then(({ startBillionaireStage }) => {
      if (stageRef.current && !gameRef.current) gameRef.current = startBillionaireStage(stageRef.current);
    });
    const audio = audioRef.current;
    return () => { gameRef.current?.destroy(true); gameRef.current = null; audio.dispose(); };
  }, []);

  // Kasa sayacı: yeni değere sayarak ulaşır ve tıkırdar.
  useEffect(() => {
    const target = economy.bank;
    if (prefersReducedMotion()) { setDisplayBank(target); return; }
    let raf = 0; const from = displayBank; const started = performance.now();
    if (from === target) return;
    const step = (now: number) => {
      const t = Math.min(1, (now - started) / BANK_COUNT_MS); const eased = 1 - Math.pow(1 - t, 3);
      setDisplayBank(Math.round(from + (target - from) * eased));
      if (target > from) audioRef.current.moneyTick();
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // displayBank yalnız başlangıç değeri olarak okunur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [economy.bank]);

  const clearTimer = () => {
    if (pendingTimer.current) window.clearTimeout(pendingTimer.current.id);
    pendingTimer.current = null;
  };
  const scheduleAction = (milliseconds: number, callback: () => void) => {
    clearTimer();
    const wrapped = () => { pendingTimer.current = null; callback(); };
    pendingTimer.current = { remaining: milliseconds, callback: wrapped, id: window.setTimeout(wrapped, milliseconds) };
  };
  useEffect(() => () => clearTimer(), []);

  const setStageMood = (mood: BillionaireStageMood) => EventBus.emit('billionaire-stage-mood', mood);

  /** Logolu geçişi gösterir; süre dolunca veya dokununca `next` çalışır. */
  const showTransition = (info: BillionaireTransitionInfo, next: () => void, withSound = true) => {
    const run = () => { setTransition(null); next(); };
    setTransition({ ...info, next: run }); setPhase('transition'); setStageMood('transition');
    if (withSound) audioRef.current.transition();
    setLiveMessage(`${info.title}. ${info.subtitle}`);
    scheduleAction(TRANSITION_MS, run);
  };
  const skipTransition = () => {
    if (phase !== 'transition' || !transition) return;
    clearTimer(); transition.next();
  };

  const finish = (finalEconomy: BillionaireEconomyState, questions: BillionaireQuestionResult[], bonuses: BillionaireBonusResult[]) => {
    setEconomy(finalEconomy); setQuestionResults(questions); setBonusResults(bonuses); setPhase('final'); setStageMood('final');
    setLiveMessage(`Yarışma tamamlandı. Final kasası ${formatTurkishLira(finalEconomy.bank)}.`); audioRef.current.finish();
    if (resultSent.current) return;
    resultSent.current = true;
    onFinishedRef.current?.({
      score: finalEconomy.bank, lives: 0, correct: finalEconomy.correct, wrong: finalEconomy.wrong,
      attempts: finalEconomy.answered, completed: true,
      details: { templateKey: 'billionaire-quiz', finalBank: finalEconomy.bank, unusedMultiplier: finalEconomy.pendingMultiplier, questions, bonuses },
    });
  };

  const revealQuestion = (index: number) => {
    setQuestionIndex(index); setSelectedChoice(null); setSelectedBonus(null); setPhase('question-enter'); setStageMood('question');
    audioRef.current.startBed(config.questions.length > 1 ? index / (config.questions.length - 1) : 0);
    scheduleAction(420, () => { questionStartedAt.current = performance.now(); setPhase('awaiting-answer'); setLiveMessage(`${index + 1}. soru açıldı.`); });
  };
  const openQuestion = (index: number, withSound = true) => {
    const question = config.questions[index];
    showTransition({ kind: 'question', title: `${index + 1}. SORU`, subtitle: `${formatTurkishLira(question.basePrize)} değerinde` }, () => revealQuestion(index), withSound);
  };

  const runSwap = (plan: BonusShufflePlan, step: number) => {
    if (step >= plan.swaps.length) { setPhase('bonus-awaiting-pick'); setLiveMessage('Takip ettiğin kartı seç.'); return; }
    setShuffleStep(step + 1); setPhase('bonus-shuffle'); audioRef.current.cardSlide();
    scheduleAction(bonusShuffleTiming.swapMs + bonusShuffleTiming.swapPauseMs, () => runSwap(plan, step + 1));
  };
  const startBonus = (deck: BillionaireBonusEffect[], round: number) => {
    const plan = planBonusShuffle(round, Math.random);
    setActiveDeck(deck); setShufflePlan(plan); setShuffleStep(0); setSelectedBonus(null);
    setPhase('bonus-preview'); setStageMood('bonus'); audioRef.current.cardFlip();
    setLiveMessage('Kartlara iyi bak: iyi kartın yerini aklında tut.');
    scheduleAction(bonusShuffleTiming.previewMs, () => {
      setPhase('bonus-flip'); audioRef.current.cardFlip(); setLiveMessage('Kartlar kapanıyor ve yer değiştiriyor. Gözünle takip et!');
      scheduleAction(bonusShuffleTiming.flipMs, () => runSwap(plan, 0));
    });
  };

  const continueAfterQuestion = (nextEconomy: BillionaireEconomyState, questions: BillionaireQuestionResult[], bonuses: BillionaireBonusResult[]) => {
    if (questionIndex + 1 >= config.questions.length) {
      audioRef.current.stopBed();
      showTransition({ kind: 'final', title: 'FİNAL', subtitle: 'Kasan açılıyor…' }, () => finish(nextEconomy, questions, bonuses));
      return;
    }
    const roundIndex = schedule.indexOf(questionIndex + 1);
    if (roundIndex >= 0) {
      audioRef.current.stopBed();
      const deck = createBonusDeck(seedKey, roundIndex + 1);
      showTransition({ kind: 'bonus', title: `${roundIndex + 1}. BONUS TURU`, subtitle: 'Kartı gözünle takip et!' }, () => startBonus(deck, roundIndex + 1));
      return;
    }
    openQuestion(questionIndex + 1);
  };

  const chooseAnswer = (choiceIndex: number) => {
    if (phase !== 'awaiting-answer' || !current) return;
    audioRef.current.unlock(); audioRef.current.lock(); setSelectedChoice(choiceIndex); setPhase('lock'); setStageMood('lock');
    setLiveMessage(`${LETTERS[choiceIndex]} şıkkı kilitlendi.`);
    const correctChoiceIndex = current.choices.findIndex((choice) => choice.correct);
    const correct = choiceIndex === correctChoiceIndex;
    const bankBefore = economy.bank;
    const calculation = applyQuestionAnswer(economy, current.basePrize, correct);
    pendingAnswer.current = {
      calculation,
      record: {
        level: current.id,
        durationMs: Math.max(0, Math.round(performance.now() - questionStartedAt.current)),
        selectedChoiceIndex: choiceIndex, correctChoiceIndex, correct,
        basePrize: current.basePrize, appliedMultiplier: calculation.appliedMultiplier, awardedAmount: calculation.awardedAmount,
        bankBefore, bankAfter: calculation.state.bank,
        questionPrompt: current.prompt || 'Görsel soru',
        selectedAnswer: answerLabel(current.choices[choiceIndex]?.label ?? '', Boolean(current.choices[choiceIndex]?.mediaUrl)),
        correctAnswer: answerLabel(current.choices[correctChoiceIndex]?.label ?? '', Boolean(current.choices[correctChoiceIndex]?.mediaUrl)),
      },
    };
    scheduleAction(LOCK_SUSPENSE_MS, revealAnswer);
  };

  const revealAnswer = () => {
    const answered = pendingAnswer.current; if (!answered) return;
    pendingAnswer.current = null;
    const { record, calculation } = answered;
    const nextQuestions = [...questionResults, record];
    setEconomy(calculation.state); setQuestionResults(nextQuestions); setPhase('feedback'); setStageMood(record.correct ? 'correct' : 'wrong');
    if (record.correct) { audioRef.current.correct(); setLiveMessage(`Doğru cevap. ${formatTurkishLira(calculation.awardedAmount)} kasaya eklendi.`); }
    else { audioRef.current.wrong(); setLiveMessage(`Yanlış cevap. Doğru cevap ${record.correctAnswer}.`); }
    scheduleAction(QUESTION_FEEDBACK_MS, () => continueAfterQuestion(calculation.state, nextQuestions, bonusResults));
  };

  const chooseBonus = (cardIndex: number) => {
    if (phase !== 'bonus-awaiting-pick' || !activeDeck[cardIndex]) return;
    setSelectedBonus(cardIndex); setPhase('bonus-reveal');
    const round = schedule.indexOf(questionIndex + 1) + 1;
    const effect = activeDeck[cardIndex];
    const multiplierBefore = economy.pendingMultiplier;
    const bankBefore = economy.bank;
    const calculation = applyBonusEffect(economy, effect, config.questions[questionIndex + 1].basePrize);
    const record: BillionaireBonusResult = {
      round, afterQuestion: questionIndex + 1, selectedCardIndex: cardIndex, effect,
      amountDelta: calculation.amountDelta, bankBefore, bankAfter: calculation.state.bank,
      multiplierBefore, multiplierAfter: calculation.state.pendingMultiplier,
    };
    const nextBonuses = [...bonusResults, record];
    setEconomy(calculation.state); setBonusResults(nextBonuses);
    audioRef.current.cardFlip(); audioRef.current.reveal(calculation.amountDelta >= 0 && effect !== 'lose-quarter');
    setStageMood(effect === 'lose-quarter' ? 'wrong' : 'correct');
    setLiveMessage(bonusLabel(effect, config.questions[questionIndex + 1].basePrize));
    scheduleAction(BONUS_REVEAL_MS, () => openQuestion(questionIndex + 1));
  };

  const start = () => {
    audioRef.current.unlock(); audioRef.current.intro(); runStartedAt.current = performance.now(); setLiveMessage('Yarışma başladı.');
    openQuestion(0, false);
  };
  const restart = () => {
    clearTimer(); audioRef.current.stopBed(); resultSent.current = false; pendingAnswer.current = null;
    setQuestionIndex(0); setEconomy(initialEconomyState()); setDisplayBank(0); setQuestionResults([]); setBonusResults([]);
    setSelectedChoice(null); setSelectedBonus(null); setActiveDeck([]); setShufflePlan(null); setShuffleStep(0); setTransition(null);
    setPhase('intro'); setStageMood('intro'); setLiveMessage('Yarışma sahnesi hazır.');
    runStartedAt.current = performance.now(); questionStartedAt.current = performance.now();
  };

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'm') { const muted = audioRef.current.toggleMuted(); setMusicMuted(muted); setFxMuted(muted); return; }
      if (phase === 'transition' && (key === 'enter' || key === ' ')) { event.preventDefault(); skipTransition(); return; }
      const number = Number(event.key);
      if (phase === 'awaiting-answer' && number >= 1 && number <= 4) chooseAnswer(number - 1);
      if (phase === 'bonus-awaiting-pick' && number >= 1 && number <= BONUS_CARD_COUNT) chooseBonus(number - 1);
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  });

  const cardSlots = shufflePlan ? slotsAfter(shufflePlan, shuffleStep) : Array.from({ length: BONUS_CARD_COUNT }, (_, index) => index);
  const movingPair = phase === 'bonus-shuffle' && shufflePlan ? shufflePlan.swaps[shuffleStep - 1] : undefined;
  const cardFaceUp = (cardIndex: number) => phase === 'bonus-preview' || (phase === 'bonus-reveal' && selectedBonus === cardIndex);

  useEffect(() => {
    window.render_game_to_text = () => JSON.stringify({
      template: 'billionaire-quiz', phase, question: Math.min(questionIndex + 1, config.questions.length), totalQuestions: config.questions.length,
      choices: current?.choices.map((choice, index) => ({ index: index + 1, label: choice.label || 'Görsel seçenek' })) ?? [],
      selectedChoice, correctChoice: phase === 'feedback' ? (current?.choices.findIndex((choice) => choice.correct) ?? -1) + 1 : null,
      basePrize: current?.basePrize ?? 0, bank: economy.bank, displayBank, pendingMultiplier: economy.pendingMultiplier,
      transition: transition ? { kind: transition.kind, title: transition.title } : null,
      bonusRound: phase.startsWith('bonus') ? schedule.indexOf(questionIndex + 1) + 1 : null,
      bonusCards: phase.startsWith('bonus') ? cardSlots.map((slot, card) => ({ card: card + 1, slot: slot + 1, faceUp: cardFaceUp(card), effect: cardFaceUp(card) ? activeDeck[card] : null })) : null,
      shuffleStep, swaps: shufflePlan?.swaps.length ?? 0,
      selectedBonus, stage: (gameRef.current?.scene.getScene('BillionaireStage') as { getDebugState?: () => unknown } | undefined)?.getDebugState?.(),
    });
    window.advanceTime = (milliseconds: number) => {
      const pending = pendingTimer.current;
      if (!pending) return;
      pending.remaining -= Math.max(0, milliseconds);
      if (pending.remaining <= 0) { window.clearTimeout(pending.id); pendingTimer.current = null; pending.callback(); }
    };
    return () => { window.render_game_to_text = () => '{}'; window.advanceTime = () => undefined; };
  });

  const correctChoice = current?.choices.findIndex((choice) => choice.correct) ?? -1;
  const mistakes = questionResults.filter((result) => !result.correct);
  const showQuestion = ['question-enter', 'awaiting-answer', 'lock', 'feedback'].includes(phase) && current;
  const bonusPhase = phase.startsWith('bonus');
  return (
    <FullscreenGameShell gameRef={gameRef} className="billionaire-shell" toolbar={<>
      <strong className="billionaire-question-chip">SORU {Math.min(questionIndex + 1, config.questions.length)} / {config.questions.length}</strong>
      <span className="billionaire-multiplier">{economy.pendingMultiplier > 1 ? `SONRAKİ DOĞRU x${economy.pendingMultiplier}` : 'BONUS HAZIR'}</span>
      <span className="billionaire-bank"><small>GÜNCEL KASA</small>{formatTurkishLira(displayBank)}</span>
      <button className="billionaire-sound" type="button" onClick={() => setMusicMuted(audioRef.current.toggleMusic())} aria-pressed={musicMuted} title="Müzik">{musicMuted ? '🔇' : '🎵'} <b>{musicMuted ? 'Müzik kapalı' : 'Müzik'}</b></button>
      <button className="billionaire-sound" type="button" onClick={() => setFxMuted(audioRef.current.toggleEffects())} aria-pressed={fxMuted} title="Efektler (M: hepsi)">{fxMuted ? '🔇' : '🔊'} <b>{fxMuted ? 'Efekt kapalı' : 'Efekt'}</b></button>
    </>}>
      <div className={`billionaire-stage phase-${phase}`} role="application" aria-label={label}>
        <div className="billionaire-phaser" ref={stageRef} aria-hidden="true" />
        <div className="billionaire-vignette" aria-hidden="true" />
        <p className="sr-only" aria-live="assertive">{liveMessage}</p>

        {phase === 'intro' && <section className="billionaire-intro-panel">
          <BillionaireEmblem className="intro" />
          <p className="eyebrow">Bilgi · Şans · Büyük Kasa</p>
          <p>{config.questions.length} soru · Süre sınırı yok · {schedule.length} bonus turu</p>
          <button type="button" onClick={start}>Yarışmayı başlat</button>
        </section>}

        {transition && <BillionaireTransition info={transition} onSkip={skipTransition} />}

        {showQuestion && <div className="billionaire-play-layout">
          <aside className="billionaire-ladder" aria-label="Para merdiveni">
            {[...config.questions].reverse().map((question) => {
              const index = question.id - 1;
              const status = index === questionIndex ? (phase === 'feedback' ? (questionResults[index]?.correct ? 'active won-now' : 'active missed-now') : 'active') : index < questionIndex ? questionResults[index]?.correct ? 'won' : 'missed' : 'future';
              return <div className={status} key={question.id}><span>{question.id}</span><strong>{formatTurkishLira(question.basePrize)}</strong></div>;
            })}
          </aside>
          <main className="billionaire-question-area">
            <section className="billionaire-question-panel">
              <small>{formatTurkishLira(current.basePrize)} DEĞERİNDEKİ SORU</small>
              {current.promptMediaUrl && <img src={current.promptMediaUrl} alt="Soru görseli" />}
              {current.prompt && <h2>{current.prompt}</h2>}
            </section>
            <div className="billionaire-choices">
              {current.choices.map((choice, index) => {
                const revealed = phase === 'feedback';
                const state = revealed && index === correctChoice ? 'correct' : revealed && index === selectedChoice ? 'wrong' : index === selectedChoice ? 'locked' : phase === 'lock' ? 'dimmed' : '';
                return <button className={state} type="button" key={choice.id} disabled={phase !== 'awaiting-answer'} onClick={() => chooseAnswer(index)}>
                  <span>{LETTERS[index]}</span>{choice.mediaUrl && <img src={choice.mediaUrl} alt="" />}{choice.label && <strong>{choice.label}</strong>}
                </button>;
              })}
            </div>
            {phase === 'lock' && <div className="billionaire-feedback lock" role="status">CEVAP KİLİTLENDİ…</div>}
            {phase === 'feedback' && <div className={`billionaire-feedback ${selectedChoice === correctChoice ? 'correct' : 'wrong'}`} role="status">
              {selectedChoice === correctChoice ? `DOĞRU! +${formatTurkishLira(questionResults.at(-1)?.awardedAmount ?? 0)}` : `DOĞRU CEVAP: ${LETTERS[correctChoice]}`}
            </div>}
          </main>
        </div>}

        {bonusPhase && <section className="billionaire-bonus-panel">
          <p className="eyebrow">Sürpriz Kasa · {schedule.indexOf(questionIndex + 1) + 1}. bonus turu</p><h2>KARTI TAKİP ET</h2>
          <p>{phase === 'bonus-preview' ? 'İyi kartın yerini aklında tut!' : phase === 'bonus-flip' || phase === 'bonus-shuffle' ? 'Kartlar karışıyor… gözünü ayırma!' : phase === 'bonus-awaiting-pick' ? 'Takip ettiğin kartı seç' : liveMessage}</p>
          <div className="billionaire-bonus-cards">
            {cardSlots.map((slot, cardIndex) => {
              const face = activeDeck[cardIndex] ? bonusFace(activeDeck[cardIndex]) : null;
              const lift = movingPair ? (slot === movingPair[0] ? -1 : slot === movingPair[1] ? 1 : 0) : 0;
              const style = { '--slot': slot, '--lift': lift } as CSSProperties;
              const classes = [cardFaceUp(cardIndex) ? 'face-up' : '', lift ? 'moving' : '', selectedBonus === cardIndex ? 'picked' : '', face ? `tone-${face.tone}` : ''].join(' ');
              return <button type="button" key={cardIndex} style={style} className={classes} disabled={phase !== 'bonus-awaiting-pick'} onClick={() => chooseBonus(cardIndex)}
                aria-label={phase === 'bonus-awaiting-pick' ? `${slot + 1}. kart` : undefined}>
                <span className="billionaire-card-inner">
                  <span className="billionaire-card-back"><b>?</b><small>{slot + 1}</small></span>
                  <span className="billionaire-card-front">{face && <><b>{face.big}</b><small>{face.small}</small></>}{phase === 'bonus-reveal' && selectedBonus === cardIndex && <em>{bonusLabel(activeDeck[cardIndex], config.questions[questionIndex + 1].basePrize)}</em>}</span>
                </span>
              </button>;
            })}
          </div>
        </section>}

        {phase === 'final' && <section className="billionaire-final-panel">
          <BillionaireEmblem className="final" />
          <p className="eyebrow">Final Kasası</p><h2>{formatTurkishLira(displayBank)}</h2>
          <p>{economy.correct} doğru · {economy.wrong} yanlış · {bonusResults.length} bonus turu</p>
          <p>Toplam süre {Math.max(1, Math.round((performance.now() - runStartedAt.current) / 1000))} saniye</p>
          {economy.pendingMultiplier > 1 && <p className="unused-multiplier">Kullanılmadan kalan çarpan: x{economy.pendingMultiplier}</p>}
          {mistakes.length > 0 && <div className="billionaire-review"><strong>Tekrar göz atalım</strong><ul>{mistakes.map((result) => <li key={result.level}>S{result.level}: {result.questionPrompt} — Doğru: {result.correctAnswer}</li>)}</ul></div>}
          {restartEnabled && <button type="button" onClick={restart}>Yeniden oyna</button>}
        </section>}
      </div>
    </FullscreenGameShell>
  );
}
